import React, {useEffect, useRef, useState} from "react";
import {Button, Input, notification, Segmented, Select, Space, Spin, Table, Tag, Tooltip} from "antd";
import {useNavigate} from "react-router-dom";
import {
    CaretRightOutlined,
    CheckCircleOutlined,
    CloseOutlined,
    CommentOutlined,
    DeleteOutlined,
    EllipsisOutlined, ExclamationOutlined,
    EyeOutlined,
    FilterOutlined,
    HistoryOutlined,
    InfoCircleOutlined,
    MailOutlined,
    NumberOutlined,
    PlusOutlined,
    ReloadOutlined,
    UserSwitchOutlined,
    WarningOutlined,
} from "@ant-design/icons";
import moment from "moment";
import "./InvoiceList.css";
import {useAuth} from "oidc-react";
import Popup from "../../../components/popups/SearchPopUp";
import InvoicePopup from "../../../components/popups/AddInvoicePopup";
import SEO from "../../../components/SEO"
import {formatDate} from "../../../utils/datetime.utils";
import {
    ProcessingStatusCollections,
    ProcessingStatusColorsEnum,
    ProcessingStatusEnum,
    ProcessingStatusShortCodeEnum
} from "../../../constants/status.constants";
import {pluralize, upperCaseFirstLetter, userInitial, userInitialBackgroundColor} from "../../../utils/string.utils";
import {hasAny} from "../../../utils/array.utils";
import {Search} from "../../../constants/session.constants";
import {
    AddOrUpdateSessionObject,
    GetSessionObject,
    RemoveSessionData,
    updateSessionData
} from "../../../utils/storage.utils";
import UpdateBulkInvoiceModal from "../../../components/popups/Invoice/UpdateInvoice/UpdateBulkInvoiceModal";
import ActionMenu from "../../../components/Menu/Common/InvoiceActionMenu";
import {jwtDecode} from "jwt-decode";
import SharedService from "../../../services/shared/service";
import BulkRejectInvoiceModal from "../../../components/popups/Invoice/UpdateInvoice/BulkRejectInvoiceModal";
import BulkDeleteInvoiceModal from "../../../components/popups/DeleteInvoicePopup/BulkDeleteInvoicesModal";
import {EmailImportanceColor} from "../../../constants/color.constants";

const {Option} = Select;

function useSelectedItems(selectedItems, fetchInvoiceSearchData) {
    const prevSelectedItemsRef = useRef([]);

    useEffect(() => {
        if (
            prevSelectedItemsRef.current?.length > 0 &&
            selectedItems?.length === 0
        ) {
            fetchInvoiceSearchData();
        }
        prevSelectedItemsRef.current = selectedItems;
    }, [selectedItems, fetchInvoiceSearchData]);
}

const InvoiceList = ({
                         currentItemIndex,
                         invoiceData,
                         setinvoiceData,
                         flag,
                         setlistPagination,
                         listPagination,
                     }) => {

    const pageSEOSettings = {
        title: "Invoice List",
    };

    const defaultSearchFilters = {
        customerId: "",
        customerType: "",
        vesselId: "",
        status: "ToBeProcessed",
        senderEmail: "",
        supplierId: "",
        supplierEmail: "",
        fromDate: "",
        toDate: "",
        referenceId: null,
        emailReferenceId: null,
        invoiceNumber: null,
        days: null,
        selectedItems: [],
    }

    const [user, setUser] = useState(null);
    const [loading, setloading] = useState(false);
    const [isPopupOpen, setPopupOpen] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const [showPopup, setShowPopup] = useState(false);
    const [selectedOption, setSelectedOption] = useState(GetSessionObject(Search.InvoiceListing)?.status ?? defaultSearchFilters.status);
    const [sortedInfo, setSortedInfo] = useState({
        columnKey: "createdOn",
        field: "createdOn",
        order: "descend",
    });
    const [inputValues, setInputValues] = useState(GetSessionObject(Search.InvoiceListing) ?? defaultSearchFilters);
    const {userData, userManager} = useAuth();
    const [token, setToken] = useState(null);
    const navigate = useNavigate();

    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);

    const [isInvoiceRejectModalVisible, setIsInvoiceRejectModalVisible] = useState(false);
    const [isPopupDeleteVisible, setIsPopupDeleteVisible] = useState(false);
    const [isUpdateInvoiceOnHoldModalVisible, setIsUpdateInvoiceOnHoldModalVisible] = useState(false);
    const [isUpdateInvoiceModalVisible, setIsUpdateInvoiceModalVisible] = useState(false);

    const actionItems = [
        {
            key: 'reject',
            icon: <CloseOutlined/>,
            label: `Reject (${selectedRows.filter(x => ![ProcessingStatusEnum.Rejected, ProcessingStatusEnum.InvoiceCreated].includes(x.processingStatus)).length})`,
            disabled: !user || selectedRows.filter(x => ![ProcessingStatusEnum.Rejected, ProcessingStatusEnum.InvoiceCreated].includes(x.processingStatus)).length === 0,
            action: () => setIsInvoiceRejectModalVisible(true)
        },
        {
            key: 'delete',
            icon: <DeleteOutlined/>,
            label: `Delete (${selectedRows.filter(x => ![ProcessingStatusEnum.InvoiceCreated].includes(x.processingStatus)).length})`,
            disabled: !user || selectedRows.filter(x => ![ProcessingStatusEnum.InvoiceCreated].includes(x.processingStatus)).length === 0,
            danger: true,
            action: () => setIsPopupDeleteVisible(true)
        },
        {
            key: 'onhold',
            icon: <WarningOutlined/>,
            label: `Mark as On Hold (${selectedRows.filter(x => ![ProcessingStatusEnum.OnHold, ProcessingStatusEnum.InvoiceCreated].includes(x.processingStatus)).length})`,
            disabled: !user || selectedRows.filter(x => ![ProcessingStatusEnum.OnHold, ProcessingStatusEnum.InvoiceCreated].includes(x.processingStatus)).length === 0,
            className: 'menu-item-warning',
            action: () => setIsUpdateInvoiceOnHoldModalVisible(true)
        },
        {
            key: 'complete',
            icon: <CheckCircleOutlined/>,
            label: `Mark as Complete (${selectedRows.filter(x => ![ProcessingStatusEnum.InvoiceCreated].includes(x.processingStatus)).length})`,
            disabled: !(user?.isAdmin || user?.isManager) || selectedRows.filter(x => ![ProcessingStatusEnum.InvoiceCreated].includes(x.processingStatus)).length === 0,
            action: () => setIsUpdateInvoiceModalVisible(true)
        }
    ];

    const rowSelection = {
        selectedRowKeys,
        onChange: (keys, rows) => {
            setSelectedRowKeys(keys);
            setSelectedRows(rows);
        }
    };

    useEffect(() => {
        if (userData) {
            if (token !== userData.access_token) {
                setToken(userData.access_token);
                fetchInvoiceSearchData()
            }
        }
    }, [userData?.access_token]);

    useEffect(() => {
        if (token) {
            const decodedToken = jwtDecode(token);

            SharedService.get({token: token, url: "user"})
                .then(response => {
                    if (response) {
                        setUser({...response, token, userName: decodedToken?.name});
                    }
                });
        }
    }, [token, user?.email, user?.userName]);

    useEffect(() => {
        const sessionData = GetSessionObject(Search.InvoiceListing);

        if (sessionData) {
            setInputValues(sessionData);
        }

        setSelectedOption(sessionData?.status ?? defaultSearchFilters.status)
        fetchInvoiceSearchData();

    }, []);

    useEffect(() => {
        if (listPagination.totalItemCount !== 0 && selectedOption) {
            fetchInvoiceSearchData();
        }
    }, [listPagination.pageNumber, sortedInfo]);

    const isFirstRenderRef = useRef(true);
    useEffect(() => {
        if (isFirstRenderRef.current) {
            isFirstRenderRef.current = false;
            return;
        }
        listPagination.pageNumber = 1;
        fetchInvoiceSearchData();
    }, [selectedOption]);

    const refreshAccessToken = async () => {
        try {
            await userManager.signinSilent();
        } catch (error) {
        }
    };

    useEffect(() => {
        const refreshInterval = setInterval(() => {
            refreshAccessToken();
        }, 600000);

        return () => clearInterval(refreshInterval);
    }, [userManager]);

    const handleAddInvoiceClick = () => {
        setShowPopup(true);
    };

    const handleGridRefresh = () => {
        AddOrUpdateSessionObject(Search.InvoiceListing, defaultSearchFilters);
        fetchInvoiceSearchData();
    };
    const handlePopupClose = () => {
        setShowPopup(false);
    };

    const handlePopupSubmit = (values) => {
        // Handle form submission

        setShowPopup(false);
    };

    const handleOptionChange = (value) => {
        setloading(true);
        listPagination.pageNumber = 1;
        const filters = AddOrUpdateSessionObject(Search.InvoiceListing, {status: value ?? null});
        setInputValues(filters);
        setSelectedOption(value);
        setSelectedRows([]);
        setSelectedRowKeys([]);
    };

    const handleColumnClick = (record, item, index) => {

        if (record) {
            navigate(`/details/${record}`, {
                state: {
                    pageNumber: listPagination.pageNumber,
                    radioButton: selectedOption,
                    tableIndex: index
                },
            });
        }
    };

    const removeAlphabetsFromString = (value) => {
        if (value && typeof value === "string") {
            const numericValue = value.replace(/[^\d.-]/g, "");
            const number = Number(numericValue);

            if (isNaN(number)) return value;

            const formattedValue = number.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });

            const dollarSign = value.includes("$") ? "$" : "";
            return `${dollarSign}${formattedValue}`;
        }

        return value;
    };

    const handleClearSearch = async () => {
        setInputValues(defaultSearchFilters);
        RemoveSessionData(Search.InvoiceListing);
        await fetchInvoiceSearchData();
    };

    const handleIconClick = () => {
        setPopupOpen(true);
    };

    const handleClose = () => {
        setPopupOpen(false);
    };

    const fetchInvoiceSearchData = async () => {
        if (!token && !userData) return;
        setloading(true);
        try {
            const item = GetSessionObject(Search.InvoiceListing) ?? inputValues

            let data = {
                supplierId: item?.supplierId ?? "",
                supplierEmail: item?.supplierEmail ?? "",
                source: ["Email", "Upload"],
                senderEmail: item?.senderEmail ?? "",
                customerId: item?.customerId ?? "",
            };
            if (item && item?.fromDate) {
                const formattedFromDate = moment(
                    item.fromDate,
                    "DD/MM/YYYY"
                ).format("YYYY-MM-DDTHH:mm:ss.SSS");
                data.fromDate = formattedFromDate;
            }

            // Convert `toDate` to UTC and format it
            if (item && item?.toDate) {
                const formattedToDate = moment(item.toDate, "DD/MM/YYYY").format(
                    "YYYY-MM-DDTHH:mm:ss.SSS"
                );
                data.toDate = formattedToDate;
            }
            if (item && item?.days) {
                data.days = item.days;
            }
            if (item && item?.referenceId) {
                data.referenceId = item.referenceId;
            }

            if (item && item?.emailReferenceId) {
                data.emailReferenceId = item.emailReferenceId;
            }

            if (item && item?.invoiceNumber) {
                data.invoiceNumber = item.invoiceNumber;
            }

            if (item && item?.customerType) {
                data.customerType = item.customerType;
            }

            data.status = ProcessingStatusCollections[selectedOption] ?? (selectedOption ? [selectedOption] : []);

            const params = {
                pageNumber: listPagination.pageNumber,
                pageSize: listPagination.pageSize,
                select: "id,referenceId,sourceType,supplierId,supplierName,customerId,customerName,customerType,processingStatus,comment,processingComment,emailAddress,value,createdOn,createdBy,updatedBy,vesselId,vesselName,emailValidationQueueId,emailValidationQueueReferenceId,emailValidationQueueImportance,invoiceNumber,documentNotes,currencyId,invoiceMatchConfidence",
                orderBy: sortedInfo.order
                    ? `${sortedInfo.columnKey}:${sortedInfo.order === "ascend" ? "Asc" : "Desc"
                    }`
                    : "createdOn:Desc", // Concatenate columnKey and order as the orderBy value
            };

            const response = await SharedService.post({
                token: token ?? userData?.access_token,
                url: "documents/search",
                data,
                params,
            });

            setinvoiceData(response.items);
            setloading(false);

            setlistPagination({
                ...listPagination,
                totalItemCount: response.totalItemCount,
            });

            handleClose();
        } catch (error) {
            if (error.response && error.response.status === 500) {
                notification.error({
                    message: "Error",
                    description: error.response.statusText,
                });
            }
            if (error.response && error.response.status === 502) {
                notification.error({
                    message: "Error",
                    description: error.response.statusText,
                });
            }

            setloading(false);
        }
    };

    useSelectedItems(inputValues.selectedItems, fetchInvoiceSearchData);
    //table functionality

    const handleTableChange = (pagination, filters, sorter) => {
        setSortedInfo(sorter);
        setlistPagination({...listPagination, pageNumber: pagination.current, pageSize: pagination.pageSize});
    };

    const handleDataChange = (data) => {
        updateSessionData(Search.InvoiceListing, data, inputValues, setSearchValue);
        fetchInvoiceSearchData();
    }

    const handleSelectCustomerChange = (value) => {
        updateSessionData(Search.InvoiceListing, {customerType: value}, inputValues, setInputValues);
        fetchInvoiceSearchData();
    };

    //grid columns

    const columns = [
        {
            title: "#",
            dataIndex: "referenceId",
            render: (referenceId) => {
                return (
                    <Tooltip title={referenceId}>
                        <div>{referenceId}</div>
                    </Tooltip>
                );
            },
            width: 50,
            key: "referenceId",
            sorter: true,
            sortOrder: sortedInfo.columnKey === "referenceId" && sortedInfo.order,
        },
        {
            title: "Source",
            dataIndex: "sourceType",
            render: (val, item) => {
                if (!item?.emailValidationQueueId) return val?.source;

                return (
                    <a
                        onClick={() =>
                            navigate(`/emailqueues/details/${item.emailValidationQueueId}`)
                        }
                    >
                        {val?.source}
                        <div className="cell-sub">({item?.emailValidationQueueReferenceId})</div>

                        {item?.emailValidationQueueImportance && item.emailValidationQueueImportance === "high" && (
                            <Tag
                                className="!m-0 mt-1"
                                color={EmailImportanceColor[(item?.emailValidationQueueImportance ?? "normal").toLowerCase()]}
                            >
                                {upperCaseFirstLetter(item.emailValidationQueueImportance)}
                            </Tag>
                        )}
                    </a>
                );
            },
            width: 50,
            key: "sourceType.Source",
            sorter: true,
            sortOrder:
                sortedInfo.columnKey === "sourceType.Source" && sortedInfo.order,
        },
        {
            title: "Date",
            dataIndex: "createdOn",
            key: "createdOn",
            render: (date) => (
                <Tooltip title={formatDate(date, "dd-MMM-yyyy hh:mm tt")}>
                    <div>
                        <div>{formatDate(date, "dd-MMM-yyyy")}</div>
                        <div className="cell-sub">{formatDate(date, "hh:mm tt")}</div>
                    </div>
                </Tooltip>
            ),
            width: 100,
            sorter: true,
            sortOrder: sortedInfo.columnKey === "createdOn" && sortedInfo.order,
            defaultSortOrder: "Desc",
        },

        {
            title: "Supplier",
            dataIndex: "supplierName",
            key: "supplierName",
            render: (Supplier) => (
                <Tooltip title={Supplier}>
                    <span>{Supplier}</span>
                </Tooltip>
            ),
            width: 200,

            sorter: true,
            sortOrder: sortedInfo.columnKey === "supplierName" && sortedInfo.order,
        },
        {
            title: "Customer",
            dataIndex: "customerName",
            key: "customerName",

            render: (customer) => (
                <Tooltip title={customer}>
                    <span>{customer}</span>
                </Tooltip>
            ),
            width: 200,
            sorter: true,
            sortOrder: sortedInfo.columnKey === "customerName" && sortedInfo.order,
        },
        {
            title: "Status",
            dataIndex: "processingStatus",
            key: "processingStatus",
            render: (status) => (
                <Tooltip title={status}>
                    <span
                        className="status-badge"
                        style={{backgroundColor: ProcessingStatusColorsEnum[status]}}
                    >
                        {ProcessingStatusShortCodeEnum[status]}
                    </span>
                </Tooltip>
            ),
            width: 70,
            align: "center",
            sorter: true,
            sortOrder: sortedInfo.columnKey === "processingStatus" && sortedInfo.order,
        },
        {
            title: "Details",
            dataIndex: "processingComment",
            key: "ProcessingComment",
            render: (processingComment, item) => {
                if (processingComment || item.invoiceNumber || item?.value || item?.comment) {

                    const getConfidenceColor = (value) => {
                        if (value === 100) return "#ff4d4f";  // Red
                        if (value >= 60) return "#faad14";     // Amber
                        return "#52c41a";                     // Green
                    };

                    const color = getConfidenceColor(item?.invoiceMatchConfidence ?? 0);

                    return (
                        <ul style={{marginBottom: 0, listStyleType: 'none', paddingLeft: 0}}>
                            {item?.invoiceNumber && (
                                <li>
                                    <NumberOutlined/> {item.invoiceNumber}
                                </li>
                            )}
                            {item?.value && (
                                <li>
                                    <CaretRightOutlined/> {removeAlphabetsFromString(item.value)}{item.currencyId ? ` (${item.currencyId})` : ''}
                                </li>
                            )}
                            {hasAny(item?.documentNotes) && (
                                <li>
                                    <CommentOutlined/> {pluralize(item?.documentNotes, 'Comment')}
                                </li>
                            )}
                            {item?.comment && (
                                <li>
                                    <Tooltip title={item?.comment}>
                                        <InfoCircleOutlined/>{" "}
                                        <i>{item?.comment?.length > 100 ? `${item?.comment?.slice(0, 100)}...` : item?.comment}</i>
                                    </Tooltip>
                                </li>
                            )}
                            {processingComment && (
                                <li>
                                    <Tooltip title={processingComment}>
                                        <HistoryOutlined/>{" "}
                                        <i>{processingComment?.length > 100 ? `${processingComment.slice(0, 100)}...` : processingComment}</i>
                                    </Tooltip>
                                </li>
                            )}
                            {item?.invoiceMatchConfidence && item?.processingStatus !== ProcessingStatusEnum.InvoiceCreated && (
                                <li>
                                    <Tooltip title={`${item.invoiceMatchConfidence}% Duplicate Shipsure Match`}>
                                        <Tag
                                            style={{backgroundColor: color}}><i><b>{`${item.invoiceMatchConfidence}% Duplicate Shipsure Match`}</b></i></Tag>
                                    </Tooltip>
                                </li>
                            )}
                        </ul>
                    );
                }
                return null;
            },
            width: 250,
        },
        {
            title: "Editor",
            width: 90,
            key: "audit",
            render: (val, rec) => {
                const submittedBy = rec.emailAddress || rec.createdBy;
                if (!submittedBy && !rec.updatedBy) return null;

                const avatar = (identity, label) => (
                    <Tooltip key={`${label}-${identity}`} title={`${label}: ${identity}`}>
                        <span
                            className="inline-flex items-center justify-center w-7 h-7 rounded-full text-[11px] font-semibold text-white"
                            style={{backgroundColor: userInitialBackgroundColor(identity)}}
                        >
                            {userInitial(identity)}
                        </span>
                    </Tooltip>
                );

                return (
                    <div className="flex items-center gap-1.5">
                        {submittedBy && avatar(submittedBy, "Submitted by")}
                        {rec.updatedBy && rec.updatedBy !== submittedBy && avatar(rec.updatedBy, "Updated by")}
                    </div>
                );
            },
        },
        {
            title: "",
            align: "right",
            render: (record, item, index) => (
                <Tooltip title="View invoice">
                    <Button
                        size="small"
                        type="text"
                        icon={<EyeOutlined/>}
                        onClick={() => handleColumnClick(record, item, index)}
                    />
                </Tooltip>
            ),
            width: 60,
            dataIndex:
                "id",
            key:
                "Actions",
        },
    ];

    const paginationProps = {
        defaultPageSize: 20,
        showSizeChanger: true,
        total: listPagination.totalItemCount,
        pageSize: listPagination.pageSize,
        current: listPagination.pageNumber,
    };

    const tableConfig = {
        rowKey: "id",
    };

    const handleRemoveSelectedItem = async (itemIndex, key) => {

        const updatedSelectedItems = inputValues.selectedItems.map((item, index) => {
            if (index === itemIndex) {
                const updatedItem = {...item};
                delete updatedItem[key];  // delete the property here
                return updatedItem;
            }

            return item;
        });

        // Create the new inputValues object
        const newInputValues = {
            ...inputValues,
            [key]: "", // Clear the key at the root level
            selectedItems: updatedSelectedItems,
        };

        AddOrUpdateSessionObject(Search.InvoiceListing, newInputValues);
        setInputValues(newInputValues);

        const filteredItems = newInputValues.selectedItems.filter(
            (item) => !(Object.keys(item).length === 1 && Object.keys(item)[0] === "status")
        );

        if (filteredItems?.length >= 1) {
            await fetchInvoiceSearchData()
        } else {
            await handleClearSearch()
        }
    };


    return (
        <div>
            <SEO {...pageSEOSettings} />

            {/* Page header */}
            <div className="px-4 sm:px-7 py-5">
                <div className="flex items-start gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                        <div className="text-[22px] font-semibold text-[#0B1220] leading-tight">Invoices</div>
                        <div className="text-[13px] text-[#7A849A] mt-1">
                            Review, validate and process invoices from suppliers across your fleet.
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button icon={<ReloadOutlined/>} onClick={fetchInvoiceSearchData}>Refresh</Button>
                        <Button type="primary" icon={<PlusOutlined/>} onClick={handleAddInvoiceClick}>New invoice</Button>
                    </div>
                </div>
            </div>

            <Popup
                setInputValues={setInputValues}
                inputValues={inputValues}
                fetchInvoiceSearchData={fetchInvoiceSearchData}
                visible={isPopupOpen}
                onClose={handleClose}
            />
            <InvoicePopup
                fetchInvoiceSearchData={fetchInvoiceSearchData}
                visible={showPopup}
                onClose={handlePopupClose}
                onSubmit={handlePopupSubmit}
            />

            <div className="px-4 sm:px-7 pb-7 relative">
                {loading && (
                    <div className="absolute inset-0 flex justify-center items-center bg-white bg-opacity-50 z-10 rounded-[14px]">
                        <Spin size="large" className="custom-spin"/>
                    </div>
                )}

                {/* Filters row */}
                <div className="flex items-center gap-3 flex-wrap mb-4">
                    <div className="overflow-x-auto -mx-1 px-1 max-w-full sm:max-w-none">
                        <Segmented
                            value={selectedOption}
                            onChange={handleOptionChange}
                            options={[
                                {value: 'AllInvoices',             label: <span className="flex items-center gap-1.5"><span className="inline-block w-2 h-2 rounded-full bg-gray-400"/>All</span>},
                                {value: 'ToBeProcessed',           label: <span className="flex items-center gap-1.5"><span className="inline-block w-2 h-2 rounded-full" style={{backgroundColor: ProcessingStatusColorsEnum.OCRProcessed}}/>To be processed</span>},
                                {value: 'FailedInvoiceValidation', label: <span className="flex items-center gap-1.5"><span className="inline-block w-2 h-2 rounded-full" style={{backgroundColor: ProcessingStatusColorsEnum.FailedInvoiceValidation}}/>Failed</span>},
                                {value: 'UnderReview',             label: <span className="flex items-center gap-1.5"><span className="inline-block w-2 h-2 rounded-full" style={{backgroundColor: ProcessingStatusColorsEnum.UnderReview}}/>Under review</span>},
                                {value: 'OnHold',                  label: <span className="flex items-center gap-1.5"><span className="inline-block w-2 h-2 rounded-full" style={{backgroundColor: ProcessingStatusColorsEnum.OnHold}}/>On hold</span>},
                                {value: 'Rejected',                label: <span className="flex items-center gap-1.5"><span className="inline-block w-2 h-2 rounded-full" style={{backgroundColor: ProcessingStatusColorsEnum.Rejected}}/>Rejected</span>},
                                {value: 'InvoiceCreated',          label: <span className="flex items-center gap-1.5"><span className="inline-block w-2 h-2 rounded-full" style={{backgroundColor: ProcessingStatusColorsEnum.InvoiceCreated}}/>Completed</span>},
                            ]}
                        />
                    </div>
                    <Select
                        allowClear
                        placeholder="All customer types"
                        className="w-full sm:w-[200px]"
                        onChange={handleSelectCustomerChange}
                        value={inputValues.customerType || undefined}
                        onClear={() => handleDataChange({customerType: null})}
                    >
                        <Option value="vessel">Vessel customers</Option>
                        <Option value="entity">Entity customers</Option>
                        <Option value="">No customer assigned</Option>
                    </Select>
                    <div className="flex-1"/>
                    <Button icon={<FilterOutlined/>} onClick={handleIconClick}>Filters</Button>
                    <ActionMenu
                        loading={loading}
                        disabled={loading || selectedRows.length === 0}
                        menu={
                            <Tooltip title={selectedRows.length === 0 ? "Select rows to bulk action" : "Bulk options"}>
                                <Button
                                    icon={<EllipsisOutlined/>}
                                    disabled={selectedRows.length === 0}
                                >
                                    Bulk{selectedRowKeys.length > 0 ? ` (${selectedRowKeys.length})` : ''}
                                </Button>
                            </Tooltip>
                        }
                        items={actionItems}
                    />
                </div>

                {/* Applied filters strip */}
                {inputValues?.selectedItems?.length > 0 &&
                    Array.isArray(inputValues?.selectedItems) &&
                    Object.keys(inputValues?.selectedItems[0] || {}).filter(k => k !== "status").length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap mb-4 p-3 bg-white border border-[#E4E7EE] rounded-[10px]">
                        <span className="text-[12px] uppercase tracking-wider text-[#7A849A] font-semibold">Filters</span>
                        {inputValues.selectedItems.slice(0, 2).map((item, index) => (
                            <React.Fragment key={index}>
                                {Object.keys(item).filter(k => k !== "status").slice(0, 2).map((key) => (
                                    <Tag
                                        key={`${index}-${key}`}
                                        className="!m-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full"
                                        style={{backgroundColor: "#ECF9EE", color: "#1B7A31", border: "1px solid #C8E9CD"}}
                                    >
                                        <span className="font-semibold">{key}:</span>
                                        <span className="truncate max-w-[160px]">{item[key]}</span>
                                        <CloseOutlined
                                            className="cursor-pointer text-[10px]"
                                            onClick={() => handleRemoveSelectedItem(index, key)}
                                        />
                                    </Tag>
                                ))}
                            </React.Fragment>
                        ))}
                        {Object.keys(inputValues.selectedItems[0] || {}).filter(k => k !== "status").length > 2 && (
                            <Button size="small" type="link" onClick={handleIconClick}>
                                +{Object.keys(inputValues.selectedItems[0]).filter(k => k !== "status").length - 2} more
                            </Button>
                        )}
                        <div className="flex-1"/>
                        <Button size="small" type="link" onClick={handleClearSearch}>
                            Clear all
                        </Button>
                    </div>
                )}

                <Table
                    {...tableConfig}
                    rowSelection={rowSelection}
                    pagination={paginationProps}
                    columns={columns}
                    dataSource={invoiceData}
                    onChange={handleTableChange}
                    scroll={{x: 960}}
                />
            </div>

            {isPopupDeleteVisible && (
                <BulkDeleteInvoiceModal
                    visible={isPopupDeleteVisible}
                    onCancel={() => setIsPopupDeleteVisible(false)}
                    documentIds={selectedRows.filter(x => ![ProcessingStatusEnum.InvoiceCreated].includes(x.processingStatus)).map(x => x.id)}
                    user={user}
                />
            )}
            {isInvoiceRejectModalVisible && (
                <BulkRejectInvoiceModal
                    visible={isInvoiceRejectModalVisible}
                    onCancel={() => setIsInvoiceRejectModalVisible(false)}
                    documentIds={selectedRows.filter(x => ![ProcessingStatusEnum.InvoiceCreated].includes(x.processingStatus)).map(x => x.id)}
                    user={user}
                />
            )}
            {isUpdateInvoiceModalVisible && (
                <UpdateBulkInvoiceModal
                    visible={isUpdateInvoiceModalVisible}
                    onCancel={() => setIsUpdateInvoiceModalVisible(false)}
                    status={"InvoiceCreated"}
                    disableStatus={true}
                    documentIds={selectedRows.filter(x => ![ProcessingStatusEnum.InvoiceCreated].includes(x.processingStatus)).map(x => x.id)}
                    user={user}
                />
            )}
            {isUpdateInvoiceOnHoldModalVisible && (
                <UpdateBulkInvoiceModal
                    visible={isUpdateInvoiceOnHoldModalVisible}
                    onCancel={() => setIsUpdateInvoiceOnHoldModalVisible(false)}
                    status={"OnHold"}
                    disableStatus={true}
                    documentIds={selectedRows.filter(x => ![ProcessingStatusEnum.OnHold, ProcessingStatusEnum.InvoiceCreated].includes(x.processingStatus)).map(x => x.id)}
                    user={user}
                />
            )}
        </div>
    )
}


export default InvoiceList;
